import { Link } from './link';

const allowedProtocols = ['http:', 'https:', 'mailto:'];

export const TextWithLinks = ({ text }: { text: string }) => {
  if (!text) return '';

  // Check if URL is safe (only allow http/https)
  const isSafeUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return allowedProtocols.includes(urlObj.protocol);
    } catch {
      return false; // Invalid URL
    }
  };

  // Helper function to process line breaks in text
  const processLineBreaks = (textSegment: string) => {
    const lines = textSegment.split('\n');
    const result: (string | JSX.Element)[] = [];

    for (const [index, line] of lines.entries()) {
      result.push(line);
      // Add <br /> between lines (but not after the last line)
      if (index < lines.length - 1) {
        result.push(<br key={`br-${index}`} />);
      }
    }

    return result;
  };

  const parseLinks = (text: string) => {
    // Pattern to find [text](url)
    const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;

    // Split text into parts and replace links
    const result: (string | JSX.Element)[] = [];
    let currentPosition = 0;
    let foundLink;

    // Keep looking for links until none left
    while ((foundLink = linkPattern.exec(text)) !== null) {
      const linkStartPosition = foundLink.index;
      const linkEndPosition = linkStartPosition + foundLink[0].length;
      const displayText = foundLink[1]; // Text inside [brackets]
      const linkUrl = foundLink[2].trim(); // URL inside (parentheses)

      // Add any plain text before this link (with line break processing)
      if (linkStartPosition > currentPosition) {
        const plainText = text.slice(currentPosition, linkStartPosition);
        result.push(...processLineBreaks(plainText));
      }

      // Only create clickable link if URL is safe
      if (isSafeUrl(linkUrl)) {
        result.push(
          <Link key={`link-${linkStartPosition}`} href={linkUrl} isExternal>
            {displayText}
          </Link>,
        );
      } else {
        // If URL is unsafe, just show the original text (with line break processing)
        result.push(...processLineBreaks(`[${displayText}](${linkUrl})`));
      }

      // Move our position past this link
      currentPosition = linkEndPosition;
    }

    // Add any remaining text after the last link (with line break processing)
    if (currentPosition < text.length) {
      const remainingText = text.slice(currentPosition);
      result.push(...processLineBreaks(remainingText));
    }

    // Return the pieces (or original text with line breaks if no links found)
    return result.length > 0 ? result : processLineBreaks(text);
  };

  return <span style={{ whiteSpace: 'normal' }}>{parseLinks(text)}</span>;
};
